import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../styles/MainContainer.css';
import '../upcoming-events/UpcomingEventsList.css';
import '../wiki/Wiki.css';
import './WikiPage.css';
import api from '../wrapper/API';
import WikiList from '../wiki/WikiList';
import WikiEditorPopup, { WIKI_EDITOR_POPUP } from '../wiki/WikiEditorPopup';
import { wikiPriorityOptions } from '../wiki/WikiPriorityOptions';
import CreateForm from '../form/CreateForm';
import CustomComboBox from '../input/CustomComboBox';
import ActionButton from '../input/ActionButton';
import Popup from '../popup/Popup';
import OfflineBanner from '../shell/OfflineBanner';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useWikis, { removeWikiPage, setWikiEntries } from '../../hooks/useWikis';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';
import {
  validateWikiEntry, wikiApiErrorMessage, wikiEntryCountLabel,
  WIKI_ENTRY_TITLE_MAX, WIKI_PRIORITY_IMPORTANT, WIKI_PRIORITY_NORMAL,
} from '../../util/wikis';

const DELETE_POPUP = 'confirmdeletewiki';
const DELETE_ERROR_POPUP = 'wikideleteerror';
const OFFLINE_HINT = 'Offline nicht verfügbar';

const EMPTY_ENTRY_FORM = { title: '', content: '', orderNumber: '', priorityIndex: 0 };

function deleteQuestion(wiki) {
  const count = wiki?.entries?.length ?? 0;
  const name = <b>{wiki?.name}</b>;
  if (count === 0) return <>Soll das Wiki {name} dauerhaft gelöscht werden?</>;
  if (count === 1) return <>Soll das Wiki {name} mit seinem Eintrag dauerhaft gelöscht werden?</>;
  return <>Soll das Wiki {name} mit allen {count} Einträgen dauerhaft gelöscht werden?</>;
}

/**
 * Wiki page /wiki/:wikiId (tile under "Wikis" on the home page): header with icon, name and entry count, then
 * the entries. Admins rename/re-icon/move the wiki ("Bearbeiten"), delete it with all entries, and add entries
 * via the create form; everyone else only reads (the list takes the full width). Data comes from the shared
 * wiki list (useWikis, one GET /WikiPage/GetAll per page load, offline from the local copy); changes patch it
 * locally.
 */
function WikiPage() {
  const { wikiId } = useParams();
  const id = Number(wikiId);
  const navigate = useNavigate();
  const admin = isAdmin();
  const { byId, loaded, failed } = useWikis();
  const { isOffline } = useOffline();
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // After deleting, the page keeps showing the deleted wiki while it animates out (no "gibt es nicht" flash).
  const [leaving, setLeaving] = useState(false);
  const lastWikiRef = useRef(null);
  const live = loaded ? byId.get(id) ?? null : null;
  if (live) lastWikiRef.current = live;
  const wiki = live ?? (leaving ? lastWikiRef.current : null);

  // Requests may finish after leaving the page: then no navigation and no state updates here.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Delete wiki
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const askDelete = () => {
    if (!wiki) return;
    setPendingDelete(wiki);
    openPopup(DELETE_POPUP, wiki.id);
  };

  const handleConfirmDelete = () => {
    const target = pendingDelete;
    closePopup();
    if (!target) return;
    startSuccessBarLoading('Wiki wird gelöscht');
    const done = () => {
      startSuccessBarSuccess('Wiki gelöscht');
      if (aliveRef.current) {
        setLeaving(true);
        navigate(ROUTES.HOME, { replace: true });
      }
      removeWikiPage(target.id);
    };
    api.delete(`/WikiPage/Delete/${target.id}`)
      .then(done)
      .catch(err => {
        // Already deleted (e.g. by another admin): same result.
        if (err?.response?.status === 404) {
          done();
          return;
        }
        closeSuccessBar();
        if (!aliveRef.current) return;
        setDeleteError(wikiApiErrorMessage(err, 'Das Wiki konnte nicht gelöscht werden.'));
        openPopup(DELETE_ERROR_POPUP, target.id);
      });
  };

  // Create entry (admins)
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY_FORM);
  const [formError, setFormError] = useState(null);

  const setEntryField = (key, value) => setEntryForm(prev => ({ ...prev, [key]: value }));

  const handleCreateEntry = (event) => {
    event.preventDefault();
    if (!wiki) return;
    const invalid = validateWikiEntry(entryForm);
    setFormError(invalid);
    if (invalid) return;
    const target = wiki;
    const orderNumber = Number.parseInt(entryForm.orderNumber, 10);
    startSuccessBarLoading('Eintrag wird erstellt');
    api.post('/WikiEntry/Create', {
      wikiPageId: target.id,
      title: entryForm.title.trim(),
      content: entryForm.content,
      priority: entryForm.priorityIndex === 1 ? WIKI_PRIORITY_IMPORTANT : WIKI_PRIORITY_NORMAL,
      orderNumber: Number.isFinite(orderNumber) && orderNumber > 0 ? orderNumber : 0,
    })
      .then(res => {
        setWikiEntries(target.id, res.data);
        startSuccessBarSuccess('Eintrag erstellt');
        if (!aliveRef.current) return;
        setEntryForm(EMPTY_ENTRY_FORM);
        setFormError(null);
      })
      .catch(err => {
        closeSuccessBar();
        const gone = err?.response?.status === 404;
        // The wiki was deleted meanwhile: the page then says so.
        if (gone) removeWikiPage(target.id);
        if (!aliveRef.current) return;
        setFormError(gone ? 'Das Wiki existiert nicht mehr.' : wikiApiErrorMessage(err, 'Fehler beim Erstellen.'));
      });
  };

  if (!wiki) {
    let message;
    if (!loaded) {
      if (!failed) message = <p className="wikiPageMessage">Lädt …</p>;
      else if (isOffline) message = <p className="wikiPageMessage">Offline nicht verfügbar – die Wikis sind auf diesem Gerät noch nicht gespeichert.</p>;
      else message = <p className="wikiPageMessage">Das Wiki konnte nicht geladen werden.</p>;
    } else {
      message = (
        <div className="wikiPageMessage">
          <p>Dieses Wiki gibt es nicht (mehr).</p>
          <Link to={ROUTES.HOME} className="wikiPageMessageLink">
            <span className="material-symbols-rounded" aria-hidden="true">home</span>
            Zur Startseite
          </Link>
        </div>
      );
    }
    return (
      <main className="defaultMain">
        <OfflineBanner />
        {message}
      </main>
    );
  }

  const header = (
    <div className="wikiPageHeader">
      <div className="wikiPageHeaderMain">
        <span className="material-symbols-rounded wikiPageIcon" aria-hidden="true">{wiki.icon}</span>
        <div className="wikiPageHeaderText">
          <h2 className="wikiPageTitle">{wiki.name}</h2>
          <span className="wikiPageSubtitle">{wikiEntryCountLabel(wiki.entries.length)}</span>
        </div>
      </div>
      {admin && (
        <div className="wikiPageHeaderActions">
          <ActionButton
            icon="edit"
            label="Bearbeiten"
            onClick={() => openPopup(WIKI_EDITOR_POPUP, wiki.id)}
            disabled={isOffline}
            title={isOffline ? OFFLINE_HINT : 'Name, Icon und Position bearbeiten'}
          />
          <ActionButton
            icon="delete"
            type={1}
            onClick={askDelete}
            disabled={isOffline}
            title={isOffline ? OFFLINE_HINT : 'Wiki löschen'}
            ariaLabel="Wiki löschen"
          />
        </div>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <div className={`generalContainer wikiPageContainer${admin ? '' : ' isReadOnly'}`}>
        <WikiList wikiPageId={wiki.id} entries={wiki.entries} header={header} canEdit={admin} />
        {admin && (
          <CreateForm onSubmit={handleCreateEntry} icon="post_add" error={formError} tooltip="Eintrag erstellen">
            <div className="formFieldWrapper">
              <label className="formFieldLabel" htmlFor="wikiEntryTitle">Titel</label>
              <input
                id="wikiEntryTitle" required type="text" maxLength={WIKI_ENTRY_TITLE_MAX}
                placeholder="z. B. Grundlegende Sicherheitsregeln" autoComplete="off"
                value={entryForm.title} onChange={e => setEntryField('title', e.target.value)}
              />
            </div>
            <div className="formFieldWrapper">
              <label className="formFieldLabel" htmlFor="wikiEntryContent">Beschreibung</label>
              <input
                id="wikiEntryContent" required type="text" placeholder="Kurzbeschreibung …" autoComplete="off"
                value={entryForm.content} onChange={e => setEntryField('content', e.target.value)}
              />
            </div>
            <div className="formFieldWrapper">
              <label className="formFieldLabel" htmlFor="wikiEntryOrder">Sortierung</label>
              <input
                id="wikiEntryOrder" type="number" min={1} step={1} inputMode="numeric" placeholder="leer = ans Ende"
                value={entryForm.orderNumber} onChange={e => setEntryField('orderNumber', e.target.value)}
              />
            </div>
            <CustomComboBox
              label="Priorität"
              selected={entryForm.priorityIndex}
              onStatusChange={index => setEntryField('priorityIndex', index)}
            >
              {wikiPriorityOptions()}
            </CustomComboBox>
          </CreateForm>
        )}
      </div>

      {admin && <WikiEditorPopup wiki={wiki} />}
      <Popup
        name={DELETE_POPUP}
        headerText="Wiki löschen"
        confirmText="Löschen"
        type="confirmaction"
        onConfirm={handleConfirmDelete}
        withCancel
      >
        <p>{deleteQuestion(pendingDelete)}</p>
        <p className="formFieldHint">
          Das lässt sich nicht rückgängig machen – die Kachel verschwindet für alle von der Startseite.
        </p>
      </Popup>
      <Popup name={DELETE_ERROR_POPUP} headerText="Löschen fehlgeschlagen" confirmText="Ok" type="error">
        <p>{deleteError}</p>
      </Popup>
    </React.Fragment>
  );
}

export default WikiPage;
